import { readStorage, STORAGE_KEYS, writeStorage } from "./storage";

export function normalizeZip(value: unknown): string {
  const m = String(value || "").match(/\b\d{5}\b/);
  return m ? m[0] : "";
}

export interface ParsedAddress {
  street: string;
  houseNumber: string;
  zip: string;
}

export function parseStreetHouseZip(address: unknown): ParsedAddress {
  const raw = String(address || "");
  const zip = normalizeZip(raw);
  const text = raw
    .replace(/\b\d{5}\b/g, " ")
    .replace(/\bBerlin\b/gi, " ")
    .replace(/\bGermany\b/gi, " ")
    .replace(/\bDeutschland\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  const first = text.split(",")[0]!.trim();
  const m = first.match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/);
  if (!m) return { street: "", houseNumber: "", zip };
  return { street: m[1]!, houseNumber: m[2]!, zip };
}

export function classifyAddressLabel(
  label: string,
): "street" | "house" | "zip" | "address" | "" {
  const n = String(label || "").toLowerCase();
  const isAddr = n.indexOf("address") !== -1 || n.indexOf("adresse") !== -1;
  if (!isAddr) return "";
  if (
    n.indexOf("street") !== -1 ||
    n.indexOf("strasse") !== -1 ||
    n.indexOf("straße") !== -1
  )
    return "street";
  if (n.indexOf("house") !== -1 || n.indexOf("hausnummer") !== -1)
    return "house";
  if (
    n.indexOf("zip") !== -1 ||
    n.indexOf("plz") !== -1 ||
    n.indexOf("postal") !== -1
  )
    return "zip";
  return "address";
}

/** Persist whichever address parts we can extract from a Heyflow `fieldsSimple` map. */
export function saveAddressFromFields(fields: Record<string, unknown>): void {
  for (const [label, value] of Object.entries(fields)) {
    if (value == null || String(value).trim() === "") continue;
    const part = classifyAddressLabel(label);
    if (part === "street") writeStorage(STORAGE_KEYS.street, String(value));
    else if (part === "house") writeStorage(STORAGE_KEYS.house, String(value));
    else if (part === "zip")
      writeStorage(STORAGE_KEYS.zip, normalizeZip(value) || String(value));
    else if (part === "address") {
      writeStorage(STORAGE_KEYS.address, String(value));
      const p = parseStreetHouseZip(value);
      if (p.street) writeStorage(STORAGE_KEYS.street, p.street);
      if (p.houseNumber) writeStorage(STORAGE_KEYS.house, p.houseNumber);
      if (p.zip) writeStorage(STORAGE_KEYS.zip, p.zip);
    }
  }
}

/** Read the current address from sessionStorage, falling back to parsing the raw blob. */
export function readAddress(): ParsedAddress {
  const street = readStorage(STORAGE_KEYS.street);
  const house = readStorage(STORAGE_KEYS.house);
  const zip = normalizeZip(readStorage(STORAGE_KEYS.zip));
  if (street || house || zip) return { street, houseNumber: house, zip };
  return parseStreetHouseZip(readStorage(STORAGE_KEYS.address));
}
